import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, LogOut, Mail, Phone, RefreshCw, Inbox } from "lucide-react";
import { toast } from "sonner";
import { getMe, listInquiries, logout, startGoogleLogin, updateInquiryStatus } from "@/lib/api";
import { Seo } from "@/components/Seo";

const STATUSES = ["new", "contacted", "closed"];
const STATUS_STYLE = {
  new: "bg-[var(--red)] text-white",
  contacted: "bg-[var(--gold)] text-[var(--ink)]",
  closed: "bg-[var(--ink)]/10 text-[var(--ink-2)]",
};

const fmt = (iso) => new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });

const LoginCard = () => (
  <div className="w-full max-w-md rounded-[28px] border border-[var(--line)] bg-white p-10 text-center shadow-[0_30px_60px_-40px_rgba(14,61,34,0.35)]" data-testid="admin-login-card">
    <img src="/logo.png" alt="Sebastian's Produce" className="mx-auto h-20 w-auto" />
    <p className="eyebrow mt-6">Private</p>
    <h1 className="font-display mt-3 text-3xl font-bold">Inquiry dashboard</h1>
    <p className="mt-3 text-sm text-[var(--ink-2)]">Every quote request from the website, even if the email never arrived.</p>
    <button onClick={startGoogleLogin} className="btn-ink mt-8 w-full justify-center" data-testid="admin-google-login-btn">
      Sign in with Google
    </button>
  </div>
);

const InquiryCard = ({ inq, onStatus }) => (
  <article className="rounded-3xl border border-[var(--line)] bg-white p-6" data-testid={`inquiry-card-${inq.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="font-display text-xl font-bold">{inq.company}</h3>
        <p className="text-sm text-[var(--ink-2)]">{inq.name} · {inq.business_type}</p>
      </div>
      <select
        value={inq.status || "new"}
        onChange={(e) => onStatus(inq.id, e.target.value)}
        className={`rounded-full px-3 py-1.5 font-mono-label text-[10px] outline-none ${STATUS_STYLE[inq.status || "new"]}`}
        data-testid={`inquiry-status-select-${inq.id}`}
      >
        {STATUSES.map((s) => <option key={s} value={s} className="bg-white text-[var(--ink)]">{s}</option>)}
      </select>
    </div>
    <div className="mt-4 flex flex-wrap gap-3 text-sm">
      <a href={`mailto:${inq.email}`} className="inline-flex items-center gap-1.5 text-[var(--green)] hover:underline" data-testid={`inquiry-email-${inq.id}`}><Mail size={14} /> {inq.email}</a>
      {inq.phone && <a href={`tel:${inq.phone}`} className="inline-flex items-center gap-1.5 text-[var(--ink-2)] hover:underline"><Phone size={14} /> {inq.phone}</a>}
    </div>
    {inq.interests?.length > 0 && (
      <div className="mt-3 flex flex-wrap gap-1.5">
        {inq.interests.map((i) => <span key={i} className="rounded-full bg-[var(--canvas)] px-2.5 py-1 text-xs">{i}</span>)}
      </div>
    )}
    <p className="mt-4 whitespace-pre-wrap rounded-2xl bg-[var(--canvas)] p-4 text-sm leading-relaxed">{inq.message}</p>
    <p className="mt-3 font-mono-label text-[10px] text-[var(--muted)]">{fmt(inq.created_at)} · email {inq.email_sent ? "sent" : "not sent"}</p>
  </article>
);

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authState, setAuthState] = useState(location.state?.user ? true : null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authState === true) return;
    getMe().then((u) => { setUser(u); setAuthState(true); }).catch(() => setAuthState(false));
  }, [authState]);

  const load = () => {
    setLoading(true);
    listInquiries().then(setItems).catch(() => toast.error("Could not load inquiries")).finally(() => setLoading(false));
  };
  useEffect(() => { if (authState) load(); }, [authState]);

  const onStatus = async (id, status) => {
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
    try { await updateInquiryStatus(id, status); } catch { toast.error("Could not update status"); load(); }
  };

  const counts = useMemo(() => STATUSES.reduce((a, s) => ({ ...a, [s]: items.filter((x) => (x.status || "new") === s).length }), {}), [items]);
  const visible = filter === "all" ? items : items.filter((x) => (x.status || "new") === filter);

  if (authState === null) return <main className="grid min-h-screen place-items-center"><Loader2 className="animate-spin text-[var(--green)]" /></main>;
  if (authState === false) return <main className="grid min-h-screen place-items-center px-6 pt-24" data-testid="admin-page"><Seo title="Inquiry Dashboard | Sebastian's Produce" description="Private dashboard." /><LoginCard /></main>;

  return (
    <main className="min-h-screen px-6 pb-24 pt-36 lg:px-10" data-testid="admin-page">
      <Seo title="Inquiry Dashboard | Sebastian's Produce" description="Private dashboard." />
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Private · {user?.email}</p>
            <h1 className="font-display mt-3 text-4xl font-bold sm:text-5xl">Quote requests</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="btn-ghost !py-2.5" data-testid="admin-refresh-btn"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh</button>
            <button onClick={() => logout().then(() => { setAuthState(false); setUser(null); })} className="btn-ink !py-2.5" data-testid="admin-logout-btn"><LogOut size={15} /> Sign out</button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-filters">
          {["all", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`admin-filter-${s}`}
              className={`rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors ${filter === s ? "bg-[var(--ink)] text-white" : "bg-white text-[var(--ink-2)] hover:text-[var(--ink)]"}`}>
              {s} <span className="ml-1 font-mono-label text-[10px] opacity-70">{s === "all" ? items.length : counts[s]}</span>
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-4" data-testid="admin-inquiry-list">
          {visible.length === 0 && !loading && (
            <div className="rounded-3xl border border-dashed border-[var(--line)] p-12 text-center text-[var(--ink-2)]" data-testid="admin-empty">
              <Inbox className="mx-auto mb-3 text-[var(--muted)]" /> No inquiries here yet.
            </div>
          )}
          {visible.map((inq) => <InquiryCard key={inq.id} inq={inq} onStatus={onStatus} />)}
        </div>
      </div>
    </main>
  );
}
